import { useEffect, useRef, useState } from 'react';

/**
 * Generic Intersection Observer Hook
 * @param {Object} options - Intersection Observer options
 * @returns {Array} [ref, isVisible, hasBeenVisible]
 */
export const useIntersectionObserver = (options = {}) => {
  const elementRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        setIsVisible(visible);
        if (visible && !hasBeenVisible) {
          setHasBeenVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
        ...options,
      }
    );

    observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, [hasBeenVisible, options.threshold, options.rootMargin]);

  return [elementRef, isVisible, hasBeenVisible];
};

/**
 * Batch Intersection Observer Hook for multiple elements
 * @param {Function} onVisible - Callback when element becomes visible
 * @param {Object} options - Intersection Observer options
 * @returns {Function} - Function to create refs for elements
 */
export const useBatchIntersectionObserver = (onVisible, options = {}) => {
  const observerRef = useRef(null);
  const seenElementsRef = useRef(new Set());
  const pendingElementsRef = useRef(new Map());

  // Initialize observer immediately
  if (!observerRef.current) {
    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const id = entry.target.dataset.observeId;
          if (entry.isIntersecting) {
            if (id && !seenElementsRef.current.has(id)) {
              seenElementsRef.current.add(id);
              onVisible(id);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '100px',
        ...options,
      }
    );
  }

  useEffect(() => {
    // Observe any pending elements
    pendingElementsRef.current.forEach((id, element) => {
      if (observerRef.current && element) {
        element.dataset.observeId = id;
        observerRef.current.observe(element);
      }
    });
    pendingElementsRef.current.clear();

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const observe = (element, id) => {
    if (element && id) {
      if (observerRef.current) {
        element.dataset.observeId = id;
        observerRef.current.observe(element);
      } else {
        pendingElementsRef.current.set(element, id);
      }
    }
  };

  const unobserve = element => {
    if (element && observerRef.current) {
      observerRef.current.unobserve(element);
    }
  };

  return { observe, unobserve };
};
